'use client';

import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../config/theme';

const DataTableContainer = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.colors.gray[100]};
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${theme.shadows.xl};
    transform: translateY(-2px);
  }
`;

const TableHeader = styled.div`
  padding: ${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.lg};
  background: linear-gradient(135deg, ${theme.colors.gray[50]} 0%, ${theme.colors.primary[50]} 100%);
  border-bottom: 1px solid ${theme.colors.primary[200]};
`;

const TableTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary[900]};
  margin-bottom: ${theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  &::before {
    content: '📋';
    font-size: ${theme.fontSizes.lg};
  }
`;

const TableDescription = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.primary[700]};
  margin: 0;
  line-height: 1.5;
`;

const TableControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  background: ${theme.colors.gray[50]};
  border-bottom: 1px solid ${theme.colors.gray[200]};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
`;

const SearchInput = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.gray[700]};
  background: ${theme.colors.white};
  width: 250px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }

  &::placeholder {
    color: ${theme.colors.gray[400]};
  }
`;

const SortSelect = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.gray[700]};
  background: ${theme.colors.white};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.fontSizes.sm};
`;

const TableHead = styled.thead`
  background: ${theme.colors.primary[50]};
  border-bottom: 2px solid ${theme.colors.primary[200]};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderCell = styled.th<{ sortable?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  text-align: left;
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.primary[800]};
  border-bottom: 1px solid ${theme.colors.primary[200]};
  white-space: nowrap;
  cursor: ${({ sortable }) => sortable ? 'pointer' : 'default'};
  user-select: none;
  transition: background-color 0.2s ease;

  &:first-child {
    padding-left: 24px;
  }

  &:hover {
    background: ${({ sortable }) => sortable ? theme.colors.primary[100] : 'transparent'};
  }

  &::after {
    content: ${({ sortable }) => sortable ? '"⇅"' : '""'};
    margin-left: ${theme.spacing.xs};
    color: ${theme.colors.primary[400]};
    font-size: ${theme.fontSizes.xs};
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ clickable?: boolean }>`
  transition: background-color 0.2s ease;
  cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};

  &:hover {
    background: ${theme.colors.gray[50]};
  }

  &:nth-child(even) {
    background: rgba(0, 0, 0, 0.02);
  }

  &:hover:nth-child(even) {
    background: ${theme.colors.gray[50]};
  }
`;

const TableCell = styled.td<{ align?: 'left' | 'center' | 'right'; type?: 'number' | 'text' | 'status'; bold?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.gray[200]};
  text-align: ${({ align }) => align || 'left'};
  color: ${theme.colors.gray[700]};
  vertical-align: middle;
  font-weight: ${({ bold }) => bold ? theme.fontWeights.bold : theme.fontWeights.normal};
  font-family: 'Open Sans, sans-serif';

  &:first-child {
    padding-left: 24px;
  }

  ${({ type }) => {
    if (type === 'number') {
      return `
        font-family: 'Monaco', 'Menlo', monospace;
        font-weight: ${theme.fontWeights.medium};
      `;
    }
    return '';
  }}
`;

const StatusBadge = styled.span<{ status: 'success' | 'warning' | 'error' | 'info' }>`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ status }) => {
    switch (status) {
      case 'success':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
          &::before { content: '✓ '; }
        `;
      case 'warning':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
          &::before { content: '⚠ '; }
        `;
      case 'error':
        return `
          background: ${theme.colors.error}20;
          color: ${theme.colors.error};
          &::before { content: '✗ '; }
        `;
      case 'info':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[700]};
          &::before { content: 'ℹ '; }
        `;
      default:
        return '';
    }
  }}
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  background: ${theme.colors.gray[50]};
  border-top: 1px solid ${theme.colors.gray[200]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.gray[600]};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-left: auto;
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.md};
  background: ${({ active }) => active ? theme.colors.primary[500] : theme.colors.white};
  color: ${({ active }) => active ? theme.colors.white : theme.colors.gray[700]};
  cursor: pointer;
  font-size: ${theme.fontSizes.xs};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ active }) => active ? theme.colors.primary[600] : theme.colors.gray[50]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  type?: 'number' | 'text' | 'status';
  bold?: boolean;
  render?: (value: unknown, row: Record<string, unknown>) => React.ReactNode;
}

export interface TableRow {
  [key: string]: unknown;
}

export interface DataTableProps {
  title: string;
  description?: string;
  columns: TableColumn[];
  data: TableRow[];
  searchable?: boolean;
  sortable?: boolean;
  paginated?: boolean;
  pageSize?: number;
  onRowClick?: (row: TableRow) => void;
  className?: string;
}

export const DataTable: React.FC<DataTableProps> = ({
  title,
  description,
  columns,
  data,
  searchable = true,
  sortable = true,
  paginated = true,
  pageSize = 10,
  onRowClick,
  className
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    
    return data.filter(row =>
      Object.values(row).some(value =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      
      if (sortDirection === 'asc') {
        return aString < bString ? -1 : aString > bString ? 1 : 0;
      } else {
        return aString > bString ? -1 : aString < bString ? 1 : 0;
      }
    });
  }, [filteredData, sortColumn, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!paginated) return sortedData;
    
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize, paginated]);

  const totalPages = Math.ceil(sortedData?.length / pageSize);

  const handleSort = (columnKey: string) => {
    if (!sortable) return;
    
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const renderCellContent = (column: TableColumn, value: unknown, row: TableRow): React.ReactNode => {
    if (column.render) {
      return column.render(value, row);
    }

    if (column.type === 'status') {
      const status = String(value).toLowerCase();
      let statusType: 'success' | 'warning' | 'error' | 'info' = 'info';
      
      if (['success', 'active', 'completed', 'ok'].includes(status)) {
        statusType = 'success';
      } else if (['warning', 'pending', 'processing'].includes(status)) {
        statusType = 'warning';
      } else if (['error', 'failed', 'inactive'].includes(status)) {
        statusType = 'error';
      }
      
      return <StatusBadge status={statusType}>{String(value)}</StatusBadge>;
    }

    if (column.type === 'number' && typeof value === 'number') {
      return value.toLocaleString();
    }

    return String(value);
  };

  return (
    <DataTableContainer className={className}>
      <TableHeader>
        <TableTitle>{title}</TableTitle>
        {description && <TableDescription>{description}</TableDescription>}
      </TableHeader>

      <TableControls>
        {searchable && (
          <SearchInput
            type="text"
            placeholder="Search table data..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}
        
        {sortable && (
          <SortSelect
            value={sortColumn}
            onChange={(e) => setSortColumn(e.target.value)}
          >
            <option value="">No sorting</option>
            {columns
              .filter(col => col.sortable !== false)
              .map(col => (
                <option key={col.key} value={col.key}>
                  Sort by {col.label}
                </option>
              ))}
          </SortSelect>
        )}
      </TableControls>

      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              {columns.map((column) => (
                <TableHeaderCell
                  key={column.key}
                  sortable={sortable && column.sortable !== false}
                  onClick={() => handleSort(column.key)}
                >
                  {column.label}
                </TableHeaderCell>
              ))}
            </tr>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow
                key={index}
                clickable={!!onRowClick}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    type={column.type}
                    bold={column.bold}
                  >
                    {renderCellContent(column, row[column.key], row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TableFooter>
        <span>
          Showing {paginatedData?.length} of {sortedData?.length} rows
          {searchTerm && ` (filtered from ${data?.length} total)`}
        </span>
        
        {paginated && totalPages > 1 && (
          <PaginationControls>
            <PaginationButton
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <PaginationButton
                  key={page}
                  active={currentPage === page}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </PaginationButton>
              );
            })}
            
            <PaginationButton
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationControls>
        )}
      </TableFooter>
    </DataTableContainer>
  );
};
